import type { BlockPhase, SessionExercise } from '@cadencia/contracts';
import type { SetType, WeekIntent } from '../load/types.js';

type Technique = SessionExercise['technique'];

/**
 * Tecnicas avanzadas (docs/TRAINING_ENGINE.md §11, requisito §12 del prompt).
 *
 * LA REGLA DURA VA PRIMERO
 *
 * «No se usan tecnicas avanzadas solo para hacer la programacion mas
 * compleja»: el §11 la hereda de `ROL-PREPARADOR.md` y exige **una
 * justificacion registrada para incluir cualquiera**. De ahi que aqui no se
 * devuelva una tecnica a secas sino una decision con `reasonCodes`, y que el
 * caso por defecto sea `straight` con el motivo de por que NO se puso otra.
 * Una tecnica sin motivo no se prescribe.
 *
 * POR QUE `superset` NO SE PRESCRIBE EN V1
 *
 * Es la unica de las seis que no es una propiedad de las series, sino del
 * RECORRIDO de la sesion: obliga a alternar entre dos ejercicios. La pantalla
 * de sesion activa avanza ejercicio a ejercicio y serie a serie
 * (`siguientePosicion`), y prescribir una superserie que la pantalla no sabe
 * guiar seria escribir un plan que el usuario no puede seguir —peor que no
 * prescribirla—. Las otras cinco caben en las filas de `set_prescriptions` que
 * ya existen y por eso si se emiten.
 *
 * Cuando el recorrido sepa alternar, esto se quita: la decision esta aqui, en
 * un solo sitio, y `supersetGroup` sigue en el contrato esperandola.
 *
 * ESTE MODULO NO CONOCE EL RELOJ NI LA BASE
 *
 * Recibe el contexto ya resuelto y devuelve una decision. Lo que hace con ella
 * la aplicacion —escribir `technique` y expandir las series— es cosa suya.
 */

/** Techo del §11: «max. 1-2 por sesion» para el drop set. Se toma el limite alto. */
export const MAX_DROPS_POR_SESION = 2;

/** Readiness por debajo del cual el §11 prohibe el drop set. */
export const READINESS_MINIMO_PARA_DROP = 50;

/** Demanda tecnica por encima de la cual el §11 prohibe el drop set. */
export const MAX_DEMANDA_TECNICA_PARA_DROP = 3;

/** Carga axial por encima de la cual el §11 prohibe el rest-pause. */
export const MAX_CARGA_AXIAL_PARA_REST_PAUSE = 3;

/**
 * Cuantas semanas finales de acumulacion cuentan como «acumulacion tardia».
 *
 * CONVENCION declarada: el §11 dice «tardia» y no da numero. Son las DOS
 * ultimas semanas de intencion `accumulation`, contadas con
 * `accumulationWeeksLeftOf`.
 *
 * Es una cuenta y no una fraccion a proposito. Como proporcion, «las dos
 * ultimas» vale un numero distinto en cada rampa —depende de cuantas semanas de
 * intensificacion y de deload haya en medio—, asi que el umbral significaria
 * dos semanas en una fase y tres en otra sin que nada lo dijera. Ya paso una vez
 * con este mismo umbral medido sobre el bloque entero.
 */
export const DROP_ULTIMAS_SEMANAS_DE_ACUMULACION = 2;

/**
 * Cuantas series BASE representa cada fila prescrita. Inverso de `expandSets`.
 *
 * `drop` vale 0 a proposito: no es una serie base, es el arrastre de la ultima
 * efectiva. Rest-pause y myo-reps valen 1,5 porque una fila cubre el volumen de
 * una serie y media, que es la razon de ser de las dos.
 *
 * Coincide con `setTypeWeight` de `volume.ts` en cinco de los seis valores, y
 * NO es el mismo concepto: aquel cuenta volumen efectivo —donde el drop suma su
 * 0,5— y esto cuenta la prescripcion de la que salio. Unificarlos haria que un
 * drop de tres series base se leyera como tres y media.
 */
const SERIES_BASE_POR_FILA: Record<SetType, number> = {
  effective: 1,
  top: 1,
  backoff: 1,
  drop: 0,
  rest_pause: 1.5,
  myo: 1.5,
};

/**
 * Las series base de las que salio una prescripcion ya escrita.
 *
 * Hace falta para RE-decidir la tecnica de un hueco que ya tiene filas: sin
 * esto no hay forma de volver a llamar a `expandSets` sin inventarse un numero,
 * y un numero inventado se salta las bandas del §4.2 por la puerta de atras.
 *
 * Exacto para las tres tecnicas que hoy se pueden prescribir —`straight`,
 * `top_backoff` y `drop`— y para el caso canonico de tres series de rest-pause
 * o myo-reps. En esas dos, con 4 o 5 series base la ida redondea a la misma
 * cantidad de filas, asi que la vuelta no puede distinguirlas; hoy no es
 * alcanzable porque las dos exigen `timeConstrained`, que nadie pone a `true`.
 */
export function baseSetsOf(setTypes: readonly SetType[]): number {
  const total = setTypes.reduce((suma, tipo) => suma + SERIES_BASE_POR_FILA[tipo], 0);
  return Math.max(1, Math.round(total));
}

export interface TechniqueContext {
  /** Papel del ejercicio en la sesion. */
  role: 'main' | 'secondary' | 'accessory' | 'finisher';
  phase: BlockPhase;
  intent: WeekIntent;
  /**
   * Semanas de acumulacion que quedan DESPUES de esta. 0 = es la ultima.
   *
   * De `accumulationWeeksLeftOf`. Es una cuenta y no una proporcion porque la
   * regla es posicional; un campo que se llamaba `blockProgress` y se comparaba
   * con un umbral de «acumulacion tardia» es como se llego a que el drop set
   * saliera en la semana 2 de 8 o en ninguna, segun el dia.
   */
  accumulationWeeksLeft: number;
  /** Readiness del dia, 0-100. `null` si todavia no se ha hecho el check-in. */
  readiness: number | null;
  /** 1 = trivial, 5 = muy exigente tecnicamente. */
  technicalDemand: number;
  /** 1 = sin carga axial, 5 = coste sistemico alto. */
  axialLoad: number;
  isCompound: boolean;
  /** Maquina o cable: el §11 los admite para rest-pause. */
  onStableMachine: boolean;
  /** El usuario ha declarado que hoy tiene poco tiempo. */
  timeConstrained: boolean;
  /** Drop sets ya colocados en esta sesion. */
  dropsYaEnLaSesion: number;
}

export interface TechniqueDecision {
  technique: Technique;
  /** Por que se puso, o por que no se puso ninguna. Nunca vacio. */
  reasonCodes: string[];
}

function esAislamiento(ctx: TechniqueContext): boolean {
  return !ctx.isCompound && (ctx.role === 'accessory' || ctx.role === 'finisher');
}

/**
 * Decide la tecnica de UN ejercicio.
 *
 * El orden en que se prueban no es cosmetico: es el del §11, de la mas
 * estructural a la mas oportunista. Un ejercicio principal en intensificacion
 * lleva top set + back-off aunque tambien cumpliera lo de rest-pause por
 * tiempo, porque lo primero define la sesion y lo segundo la acorta.
 */
export function chooseTechnique(ctx: TechniqueContext): TechniqueDecision {
  // --- prohibiciones transversales ----------------------------------------
  // La readaptacion no lleva ninguna: `readaptation-block.ts` la define «sin
  // fallo y sin tecnicas avanzadas», y el §11 lo repite en tres de las seis
  // filas. Una descarga tampoco: su razon de ser es quitar estimulo.
  if (ctx.phase === 'readaptation') {
    return { technique: 'straight', reasonCodes: ['no_techniques_in_readaptation'] };
  }
  if (ctx.intent === 'deload' || ctx.intent === 'reintroduction') {
    return { technique: 'straight', reasonCodes: [`no_techniques_in_${ctx.intent}`] };
  }

  // --- top set + back-off --------------------------------------------------
  if (ctx.role === 'main' && ctx.intent === 'intensification') {
    return { technique: 'top_backoff', reasonCodes: ['main_lift_in_intensification'] };
  }

  // --- drop set ------------------------------------------------------------
  if (esAislamiento(ctx) && ctx.intent === 'accumulation') {
    const motivos: string[] = [];
    if (ctx.accumulationWeeksLeft >= DROP_ULTIMAS_SEMANAS_DE_ACUMULACION) {
      motivos.push('accumulation_not_late_enough');
    }
    if (ctx.dropsYaEnLaSesion >= MAX_DROPS_POR_SESION) motivos.push('drop_quota_reached');
    if (ctx.readiness !== null && ctx.readiness < READINESS_MINIMO_PARA_DROP) {
      motivos.push('readiness_too_low_for_drop');
    }
    if (ctx.technicalDemand > MAX_DEMANDA_TECNICA_PARA_DROP) {
      motivos.push('technical_demand_too_high_for_drop');
    }
    if (motivos.length === 0) {
      return { technique: 'drop', reasonCodes: ['late_accumulation_isolation'] };
    }
    // Se guardan los motivos: si mas tarde no encaja ninguna otra tecnica, el
    // `straight` sale explicando por que se descarto la que casi entraba.
    if (!ctx.timeConstrained) return { technique: 'straight', reasonCodes: motivos };
  }

  // --- rest-pause y myo-reps: las dos son para ganar tiempo ----------------
  if (ctx.timeConstrained) {
    const axialAlta = ctx.isCompound && ctx.axialLoad > MAX_CARGA_AXIAL_PARA_REST_PAUSE;
    if (!axialAlta && (esAislamiento(ctx) || ctx.onStableMachine)) {
      // Myo-reps solo en aislamiento; el rest-pause admite ademas maquina
      // estable. Cuando los dos caben se elige myo-reps, que es el que el §11
      // describe como «densificar volumen con poco tiempo».
      return esAislamiento(ctx)
        ? { technique: 'myo_reps', reasonCodes: ['isolation_short_on_time'] }
        : { technique: 'rest_pause', reasonCodes: ['stable_machine_short_on_time'] };
    }
    return {
      technique: 'straight',
      reasonCodes: [axialAlta ? 'axial_load_too_high_for_rest_pause' : 'not_suitable_for_density'],
    };
  }

  return { technique: 'straight', reasonCodes: ['no_technique_justified'] };
}

/**
 * Las series que prescribe una tecnica, a partir de las series base.
 *
 * Lo que devuelve es el `set_type` de cada fila de `set_prescriptions`, en
 * orden. `setTypeWeight` de `volume.ts` ya sabe cuanto pesa cada una, asi que
 * el volumen sale bien sin tocar nada mas —y esa aritmetica, escrita desde la
 * Fase 3, es la que hasta hoy no habia ejecutado nadie porque nadie emitia un
 * tipo distinto de `effective`—.
 *
 * `baseSets` es lo que la rampa de la fase pidio para ese ejercicio. Ninguna
 * tecnica AÑADE series efectivas: un drop set convierte la ultima serie en
 * serie + drop, y el rest-pause y las myo-reps sustituyen series por una que
 * pesa mas. Si añadieran, se saltarian las bandas de volumen del §4.2 por la
 * puerta de atras.
 */
export function expandSets(technique: Technique, baseSets: number): SetType[] {
  const series = Math.max(1, Math.round(baseSets));

  switch (technique) {
    case 'top_backoff': {
      // Una pesada y el resto por debajo. Con una sola serie no hay back-off
      // que hacer y se queda en la top.
      const backoff = Math.max(0, series - 1);
      return ['top', ...Array.from({ length: backoff }, (): SetType => 'backoff')];
    }
    case 'drop': {
      // La ultima serie efectiva arrastra su drop. El drop pesa 0,5, asi que
      // el total sube de N a N+0,5, no a N+1.
      const efectivas = Array.from({ length: series }, (): SetType => 'effective');
      return [...efectivas, 'drop'];
    }
    case 'rest_pause':
    case 'myo_reps': {
      // Sustituyen, no suman: cada fila pesa 1,5, asi que dos filas cubren el
      // volumen de tres series normales. De ahi que sirvan «cuando falta
      // tiempo».
      const filas = Math.max(1, Math.round((series * 2) / 3));
      const tipo: SetType = technique === 'rest_pause' ? 'rest_pause' : 'myo';
      return Array.from({ length: filas }, () => tipo);
    }
    case 'superset':
    case 'straight':
    default:
      return Array.from({ length: series }, (): SetType => 'effective');
  }
}

/**
 * ¿Se puede hacer HOY una serie de esta tecnica, con el readiness de hoy?
 *
 * POR QUE HACE FALTA ESTO ADEMAS DE `chooseTechnique`
 *
 * Porque las dos preguntas se hacen en momentos distintos. La tecnica se decide
 * al materializar la semana, dias antes de entrenar, cuando el readiness del
 * dia **no existe todavia**; el §11 en cambio prohibe el drop set «con
 * readiness <50», que es una condicion del momento de ejecutar.
 *
 * Resolverlo adivinando el readiness al planificar seria inventarse el dato.
 * Resolverlo no prescribiendo nunca un drop apagaria la tecnica entera, porque
 * la planificacion SIEMPRE va por delante. Asi que se decide dos veces: al
 * planificar con lo que se sabe, y al ejecutar con lo que ya se sabe. Lo que no
 * puede pasar —y es lo que esto evita— es que un drop planificado el lunes se
 * ejecute el jueves con el readiness por los suelos.
 */
export function seriePermitidaHoy(
  setType: SetType,
  readiness: number | null,
): { permitida: boolean; reasonCodes: string[] } {
  if (setType !== 'drop') return { permitida: true, reasonCodes: [] };
  if (readiness === null) return { permitida: true, reasonCodes: ['no_readiness_checkin'] };
  if (readiness < READINESS_MINIMO_PARA_DROP) {
    return { permitida: false, reasonCodes: ['readiness_too_low_for_drop'] };
  }
  return { permitida: true, reasonCodes: [] };
}

/** Como se le explica al usuario, en la pantalla de la sesion. */
export const COMO_SE_HACE: Record<SetType, string | null> = {
  effective: null,
  top: 'Serie pesada del día: deja la carga arriba y respeta el RIR.',
  backoff: 'Back-off: baja un 10–20 % respecto a la serie pesada.',
  drop: 'Drop set: sin descanso, baja la carga un 20 % y sigue hasta el RIR.',
  rest_pause: 'Rest-pause: llega al RIR, descansa 15 s y saca las que puedas. Repite dos veces.',
  myo: 'Myo-reps: serie de activación al RIR, y luego mini-series de 3–5 repeticiones con 15 s.',
};
